import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { IEventSubTypeConfiguration, IWorkspaceTile } from '../hooks/useEventSubTypeConfig';

const DEFAULT_TILE_ITEMS: readonly IWorkspaceTile[] = [
  {
    componentId: 'event_countdown',
    route: '/edit/settings',
  },
  {
    componentId: 'concierge',
    link: 'https://withjoy.typeform.com/to/fT5TU4VL',
  },
  { componentId: 'website' },
];

export const useGeneralDefaultConfig = (): IEventSubTypeConfiguration => {
  const { tq } = useTranslation('eventWorkspace');

  return useMemo<IEventSubTypeConfiguration>(
    () => ({
      category: 'corporate',
      categorySubType: 'annual_summit',
      title: 'Annual Summit',
      description: 'Event template for corporate annual summits.  Contains default 3-day schedule, etc.',
      insights: ['Additional AI friendly descriptors here to help AI have deeper context into what this event type and data structure is about'],
      location: {
        city: 'San Francisco',
        state: 'CA',
        country: 'US',
      },
      date: 'ISO 8601 date with timezone offset here',
      eventImage: 'https://withjoy.com/assets/public/proto/modern/corpor',
      eventIcon: '',
      apps: [
        {
          appId: 'website',
          label: tq`Event Site`,
          route: '/edit/website/overview',
          color: {
            start: 'rgba(82, 146, 241, 1)',
            end: 'rgba(38, 100, 194, 1)',
          },
          background: 'linear-gradient(155.59deg, #5292F1 -6.99%, #306DCA 84.39%)',
          description: tq`Design and customize your event website`,
          keywords: ['website', 'design', 'customize', 'event'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/event_site_v1.svg',
        },
        {
          label: tq`Registration`,
          route: '/edit/registration',
          color: {
            start: 'rgba(255, 211, 36, 1)',
            end: 'rgba(255, 211, 36, 1)',
          },
          background: 'linear-gradient(153.04deg, #FBAB50 10.74%, #F49325 86.35%)',
          description: tq`Manage your event RSVPs, including guest responses and dietary restrictions`,
          keywords: ['rsvp', 'manage', 'guest', 'responses', 'dietary', 'restrictions'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/registration_v1.svg',
        },
        /*
        {
          appId: 'event_schedule',
          label: tq`Schedule`,
          route: '/edit/schedule',
          color: {
            start: 'rgba(255, 225, 155, 1)',
            end: 'rgba(255, 208, 98, 1)',
          },
          background: 'linear-gradient(153.04deg, #FFDF2A 10.74%, #FFCC00 86.35%)',
          description: tq`Event schedule to define times and locations for various activities during the event`,
          keywords: ['event', 'schedule', 'agenda', 'itinerary', 'times', 'dates', 'locations'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/schedule_v1.svg',
        },
        */
        {
          appId: 'guest_list',
          label: tq`Guests`,
          link: '${eventHandle}/edit/guestlist',
          color: {
            start: 'rgba(255, 225, 155, 1)',
            end: 'rgba(255, 208, 98, 1)',
          },
          background: 'linear-gradient(140.45deg, #806BE0 0%, #492DD9 111.55%)',
          description: tq`Manage the guests attending your event, including RSVPs, dietary restrictions, and more`,
          keywords: ['guest', 'list', 'manage', 'rsvp', 'dietary', 'restrictions'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/guests_v1.svg',
        },
        {
          label: tq`Hotels`,
          route: '/edit/accommodations',
          color: {
            start: 'rgba(217, 140, 255, 1)',
            end: 'rgba(82, 41, 164, 1)',
          },
          background: 'linear-gradient(173.84deg, #FFA3A5 -2.89%, #CD74BD 96.27%)',
          description: tq`Simplify guest accommodations while saving them money`,
          keywords: ['guest', 'stays', 'accommodations', 'hotels', 'travel'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/hotels_v1.svg',
        },
        {
          label: tq`Messaging`,
          route: '/edit/messaging',
          color: {
            start: 'rgba(138, 255, 152, 1)',
            end: 'rgba(0, 205, 24, 1)',
          },
          background: 'linear-gradient(140.63deg, #7DE174 7.46%, #3AC7DA 111.59%)',
          description: tq`Communicate with your guests through messaging`,
          keywords: ['email', 'sms', 'messaging', 'communication', 'guest', 'notify', 'remind'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/messaging_v1.svg',
        },
        {
          appId: 'settings',
          label: tq`Settings`,
          route: '/edit/settings',
          color: {
            start: 'rgba(215, 220, 226, 1)',
            end: 'rgba(110, 113, 119, 1)',
          },
          background: 'linear-gradient(140.91deg, #6C6B6B 1.75%, #202020 93.35%)',
          description: tq`Event settings to manage your event details, including title, date, location, and more`,
          keywords: ['settings', 'date', 'location', 'configure', 'manage'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/settings_v1.svg',
        },
        {
          appId: 'concierge',
          label: tq`Concierge`,
          link: 'https://withjoy.typeform.com/to/fT5TU4VL',
          color: {
            start: 'rgba(82, 146, 241, 1)',
            end: 'rgba(38, 100, 194, 1)',
          },
          background: `linear-gradient(149.23deg, #25AFFA 11.66%, rgba(73, 81, 239, 0.9) 52.28%, rgba(206, 84, 179, 0.8) 86.43%)`,
          description: tq`Event settings to manage your event details, including title, date, location, and more`,
          keywords: ['settings', 'date', 'location', 'configure', 'manage'],
          iconUrl: 'https://withjoy.com/assets/public/pro/icons/concierge_v1.svg',
        },
      ],
      tiles: DEFAULT_TILE_ITEMS,
    }),
    [tq],
  );
};
